import { Box, makeStyles, Tab, Tabs } from '@material-ui/core';
import { TabContext, TabPanel } from '@material-ui/lab';
import { CheckoutSession, PaymentMethodType } from '@nemuru-eng/js-api-client';
import { useEffect } from 'react';
import { useFormContext } from 'react-hook-form';

import { DistributionCheckbox } from '@/components/shared/components/DistributionDialog/components/DistributionCheckbox';
import { DistributionTextField } from '@/components/shared/components/DistributionDialog/components/DistributionTextField';
import { MaxDistributionsAlert } from '@/components/shared/components/DistributionDialog/components/MaxDistributionsAlert';
import { MAX_ALLOWED_DISTRIBUTIONS } from '@/components/shared/components/DistributionDialog/constants/MaxAllowedDistributions';
import { usePaymentLinkDistribution } from '@/components/shared/components/DistributionDialog/hooks/usePaymentLinkDistribution';
import { DistributionMethod } from '@/components/shared/components/DistributionDialog/types/DistributionMethod';
import { Font } from '@/components/shared/components/Font';
import { Icon } from '@/components/shared/components/Icon';
import { PhoneFormDoubleField } from '@/components/shared/components/NewContextForms/PhoneFormDoubleField/PhoneFormDoubleField';
import { TextFormField } from '@/components/shared/components/NewContextForms/TextFormField';
import { FieldVariant } from '@/components/shared/components/NewContextForms/types/FieldVariant';
import { Text } from '@/components/shared/components/Text';
import { useTranslation } from '@/components/shared/hooks/useTranslation';
import { ENV_API_URL, ENV_HPP_URL } from '@/config/envVars';
import { useCustomTheme } from '@/hooks/useCustomTheme';
import { useImagePreloader } from '@/hooks/useImagePreloader';
import { useScreen } from '@/hooks/useScreen';

interface DistributionFormProps {
  checkoutSession: CheckoutSession;
  distributionMethod: DistributionMethod;
  setDistributionMethod: React.Dispatch<React.SetStateAction<DistributionMethod>>;
}

export const DISTRIBUTION_AVAILABLE_COUNTRIES_ISO2 = ['ES', 'FR', 'GB'];

export const DistributionForm = ({
  checkoutSession,
  distributionMethod,
  setDistributionMethod,
}: DistributionFormProps) => {
  const { sm } = useScreen();
  const { getTextIdTranslation } = useTranslation();
  const { hasActiveDistributionMethods, setHasActiveDistributionMethods } = usePaymentLinkDistribution();

  const { watch, resetField } = useFormContext();
  const { palette, customBorderRadius } = useCustomTheme();

  // TODO: temporary solution to decouple this form from application context
  const qrCodeUrl = `${ENV_API_URL}v2/checkout/${checkoutSession.id}/hpp/qr/`;
  const redirectUrl = checkoutSession.hpp ? `${ENV_HPP_URL}${checkoutSession.hpp.id}` : undefined;

  if (qrCodeUrl) {
    useImagePreloader([qrCodeUrl]);
  }

  const isSmsActive = watch('activeMethods.sms');
  const isEmailActive = watch('activeMethods.email');

  useEffect(() => {
    setHasActiveDistributionMethods(!!isSmsActive || !!isEmailActive);
    if (!isSmsActive) resetField('phoneNumber');
    if (!isEmailActive) resetField('email');
  }, [isSmsActive, isEmailActive]);

  const handleChange = (_, newValue: DistributionMethod) => {
    setDistributionMethod(newValue);
  };

  const tabsClasses = makeStyles({
    root: {
      minHeight: 0,
      padding: '6px',
      backgroundColor: palette.grey[100],
      borderRadius: customBorderRadius.paper,
    },
    indicator: {
      display: 'none',
    },
  })();

  const tabClasses = makeStyles({
    root: {
      minHeight: '32px',
      padding: '4px 8px',
      borderRadius: customBorderRadius.paper,
      textTransform: 'none',
    },
    selected: {
      backgroundColor: palette.common.white,
    },
  })();

  const tabPanelClasses = makeStyles({
    root: {
      padding: 0,
      marginTop: 16,
    },
  })();

  const maxDistributionsReached =
    checkoutSession.hpp?.distributions && checkoutSession.hpp.distributions.length >= MAX_ALLOWED_DISTRIBUTIONS;

  const fieldVariant = FieldVariant.form;

  const isCreditCardPaymentMethod = checkoutSession.selectedPaymentMethodType === PaymentMethodType.CREDIT_CARD;

  return (
    <Box display={'flex'} flexDirection={'column'} gridGap={2}>
      <TabContext value={distributionMethod}>
        <Tabs
          classes={tabsClasses}
          value={distributionMethod}
          variant="fullWidth"
          onChange={handleChange}
          indicatorColor="secondary"
          textColor="secondary"
        >
          <Tab
            classes={tabClasses}
            value={DistributionMethod.SEND_LINK}
            label={
              <Font
                variant={'body2'}
                style={{ fontWeight: 500, display: 'inline-flex', alignItems: 'center', gridGap: 8 }}
              >
                <Text tId={sm ? DistributionMethod.SEND_LINK : `${DistributionMethod.SEND_LINK}_SHORT`} />
                <Icon size={16}>send</Icon>
              </Font>
            }
            disabled={
              !!(checkoutSession?.hpp && checkoutSession?.hpp?.distributions.length >= MAX_ALLOWED_DISTRIBUTIONS)
            }
          />
          <Tab
            classes={tabClasses}
            value={DistributionMethod.COPY_LINK}
            disabled={isCreditCardPaymentMethod}
            label={
              <Font
                variant={'body2'}
                style={{ fontWeight: 500, display: 'inline-flex', alignItems: 'center', gridGap: 8 }}
              >
                <Text tId={sm ? DistributionMethod.COPY_LINK : `${DistributionMethod.COPY_LINK}_SHORT`} />
                <Icon size={16}>link</Icon>
              </Font>
            }
          />
          <Tab
            classes={tabClasses}
            value={DistributionMethod.SHOW_QR}
            disabled={isCreditCardPaymentMethod}
            label={
              <Font
                variant={'body2'}
                style={{ fontWeight: 500, display: 'inline-flex', alignItems: 'center', gridGap: 8 }}
              >
                <Text tId={sm ? DistributionMethod.SHOW_QR : `${DistributionMethod.SHOW_QR}_SHORT`} />
                <Icon size={16}>qr_code</Icon>
              </Font>
            }
          />
        </Tabs>
        <TabPanel classes={tabPanelClasses} value={DistributionMethod.SEND_LINK}>
          {maxDistributionsReached ? (
            <MaxDistributionsAlert
              description={
                isCreditCardPaymentMethod
                  ? 'MAX_DISTRIBUTIONS_ALERT_DESCRIPTION_CREDIT_CARD'
                  : 'MAX_DISTRIBUTIONS_ALERT_DESCRIPTION'
              }
            />
          ) : (
            <Box display={'flex'} flexDirection={'column'} gridGap={16}>
              <Font align="left" variant={'body2'}>
                <Text tId={'COMPLETE_CLIENT_DATA'} />
              </Font>
              <Box width={'100%'} bgcolor={palette.grey[100]} borderRadius={customBorderRadius.paper}>
                <Box p={2} display={'flex'} flexDirection={'row'} gridGap={16}>
                  <DistributionCheckbox name={'activeMethods.email'} labelIcon={'email'} labelTextId={'MAIL'} />
                  <DistributionCheckbox name={'activeMethods.sms'} labelIcon={'smartphone'} labelTextId={'SMS'} />
                </Box>
              </Box>
              {hasActiveDistributionMethods && (
                <Box display={'flex'} flexDirection={'column'} gridGap={16}>
                  {isEmailActive && (
                    <TextFormField
                      disabled={isCreditCardPaymentMethod}
                      name={'email'}
                      fieldVariant={fieldVariant}
                      placeholder={getTextIdTranslation('EMAIL')}
                    />
                  )}
                  {isSmsActive && (
                    <PhoneFormDoubleField
                      disabled={isCreditCardPaymentMethod}
                      availableCountriesIso2={DISTRIBUTION_AVAILABLE_COUNTRIES_ISO2}
                      fieldVariant={fieldVariant}
                    />
                  )}
                </Box>
              )}
            </Box>
          )}
        </TabPanel>
        <TabPanel classes={tabPanelClasses} value={DistributionMethod.COPY_LINK}>
          <Box display={'flex'} flexDirection={'column'} gridGap={16}>
            <Font align="left" variant={'body2'}>
              <Text tId={'COPY_LINK_AND_SHARE'} />
            </Font>
            <DistributionTextField value={redirectUrl ?? ''} copyToClipboard />
          </Box>
        </TabPanel>
        <TabPanel classes={tabPanelClasses} value={DistributionMethod.SHOW_QR}>
          <Box display={'flex'} flexDirection={'column'} gridGap={16}>
            <Font align="left" variant={'body2'}>
              <Text tId={'SHOW_QR_TO_SHARE'} />
            </Font>
            <Box width={'100%'} bgcolor={palette.grey[100]} borderRadius={customBorderRadius.paper} minHeight={220}>
              <Box p={2} display={'flex'} flexDirection={'row'} justifyContent={'center'}>
                <img
                  src={qrCodeUrl}
                  style={{
                    maxWidth: '100%',
                    maxHeight: 400,
                    width: 'auto',
                    height: 'auto',
                    borderRadius: customBorderRadius.paper,
                  }}
                />
              </Box>
            </Box>
          </Box>
        </TabPanel>
      </TabContext>
    </Box>
  );
};
